import { useState } from 'react'
import { useRouter } from '../../lib/router'
import { MarketingLayout } from './MarketingLayout'


const FAQ_SECTIONS = [
  {
    section: 'Getting started',
    items: [
      {
        q: "How long does it take to set up AutoAxxiz for my workshop?",
        a: "Most workshops are running live job cards within one business day. We handle the onboarding: we import your existing customers, configure your job card template, and connect your WhatsApp Business number. You don't need IT skills or a consultant.",
      },
      {
        q: "Do I need to install anything?",
        a: "Owners, office staff and foremen can use the web app in Chrome, Edge or Safari. Mechanics can also use the Expo mobile app for workshop-floor job and DVI workflows.",
      },
      {
        q: "Does it work during load-shedding?",
        a: "The mobile DVI workflow supports local drafts and pending media retry, so mechanics can keep inspection work recoverable when connectivity drops. A full local database sync layer remains part of future hardening.",
      },
      {
        q: "Can I import my existing data?",
        a: "Yes. We import customer and vehicle records from Excel or CSV. We'll do the import for you during onboarding — you don't deal with spreadsheets.",
      },
    ],
  },
  {
    section: 'WhatsApp integration',
    items: [
      {
        q: "Do my customers need to install anything?",
        a: "No. Your customers use ordinary WhatsApp — the same app they already have. They receive job updates, inspection reports, quotes, and invoices directly in their existing WhatsApp conversations. Nothing to download.",
      },
      {
        q: "How is this different from just messaging on WhatsApp myself?",
        a: "When you message manually, there's no audit trail, no quote record, no link to a job card. AutoAxxiz sends structured WhatsApp messages tied to job cards. Quote approvals are logged. Invoice links are tracked. If a customer disputes a quote, you have a timestamped record.",
      },
      {
        q: "What number do messages come from?",
        a: "Your workshop's own WhatsApp Business number. Customers see your business name. We help you register or connect your existing number to the Meta WhatsApp Business API during setup.",
      },
      {
        q: "How much does WhatsApp messaging cost?",
        a: "WhatsApp Business messaging is usage-based and depends on Meta's current pricing model, message type and destination market. We keep these charges visible and confirm the expected billing model during WhatsApp setup.",
      },
      {
        q: "Can customers approve quotes over WhatsApp?",
        a: "Yes. When you send a quote, the customer receives it as a formatted WhatsApp message with a one-tap approve or decline button. Their response is logged instantly on the job card — no phone call needed, no chasing.",
      },
    ],
  },
  {
    section: 'Pricing and contracts',
    items: [
      {
        q: "Is the 30-day pilot really free?",
        a: "Yes — no credit card required, no contract. We onboard your workshop fully during the pilot. If AutoAxxiz doesn't work for you, we export your data as CSV and you owe nothing. We'd rather earn your business than lock you in.",
      },
      {
        q: "Are prices ZAR or USD?",
        a: "All prices are in South African rand. Starter is R899/month, Workshop is R1,899/month, Multi-site is R3,499/location/month — all ex-VAT. There are no USD charges, no exchange rate surprises, no international transaction fees.",
      },
      {
        q: "Is there a per-mechanic or per-user fee?",
        a: "No. Starter is designed for a single owner-mechanic. Workshop and Multi-site include all users at your location. We never charge per seat.",
      },
      {
        q: "Can I cancel at any time?",
        a: "Yes. Monthly plans can be cancelled before the next billing date. Annual plans are non-refundable after 30 days but can be paused if your workshop closes temporarily. No cancellation fees.",
      },
      {
        q: "Do you offer discounts for annual payment?",
        a: "Yes — 15% off when you pay annually, invoiced upfront in rand. That's effectively 1.8 months free.",
      },
    ],
  },
  {
    section: 'Invoicing and accounting',
    items: [
      {
        q: "Are your invoices SARS-compliant tax invoices?",
        a: "Yes. Every invoice is a valid South African tax invoice: sequential INV-{year}-{sequence} numbering, your VAT registration number, the customer's details, VAT calculated at 15%, and all required SARS fields. You can hand these directly to your bookkeeper or a SARS auditor.",
      },
      {
        q: "Does AutoAxxiz integrate with Xero or Sage?",
        a: "Yes. AutoAxxiz has a built-in accounting sync dispatcher that supports Xero, Sage Business Cloud, and Zoho Books. You configure the provider once in Settings — invoices sync automatically from there.",
      },
      {
        q: "What payment methods can customers use?",
        a: "PayFast and SnapScan payment links can be sent via WhatsApp directly from the invoice. Manual payment capture is also supported for cash and EFT. Your subscription to AutoAxxiz itself is billed via PayFast.",
      },
      {
        q: "Can I track who owes me money?",
        a: "Yes. The invoice dashboard shows every open invoice, how many days overdue, and the outstanding rand amount. You record payments manually when customers EFT or pay in cash.",
      },
    ],
  },
  {
    section: 'Data, privacy, and security',
    items: [
      {
        q: "Where is my data stored?",
        a: "The production architecture is designed for South African data residency using an SA cloud region and S3/object storage for workshop media. The final provider choice is made during infrastructure setup.",
      },
      {
        q: "Is AutoAxxiz POPIA compliant?",
        a: "The product includes POPIA-oriented controls such as tenant data export, tenant scoping and planned SA-region hosting. Formal production policies, Information Officer details and retention procedures should be finalised before public launch.",
      },
      {
        q: "What happens to my data if I cancel?",
        a: "We export your full dataset (customers, vehicles, job cards, invoices, payments) in CSV format within 48 hours of a cancellation request. We then delete your data from our systems within 30 days.",
      },
      {
        q: "How is my password and sensitive data secured?",
        a: "Passwords are hashed with bcrypt. Accounting integration tokens (Xero, Sage) are encrypted at rest with AES-256-GCM. All traffic is TLS 1.3. We do not store card numbers — payments are handled by PCI-compliant providers.",
      },
    ],
  },
  {
    section: 'Features and workflow',
    items: [
      {
        q: "What is a Digital Vehicle Inspection (DVI)?",
        a: "A DVI is a structured checklist your mechanic completes when a vehicle arrives — tyres, brakes, lights, fluid levels, and any other items you configure. The completed checklist, with photos, is sent to the customer via WhatsApp and attached to the job card as a PDF. It creates transparency, reduces disputes, and often surfaces additional work the customer hadn't noticed.",
      },
      {
        q: "Can I manage multiple bays?",
        a: "Yes — Workshop and Multi-site plans show a live bay board. Each bay has a status (available, occupied, waiting for parts) and an assigned job card. The foreman sees the whole workshop at a glance without walking the floor.",
      },
      {
        q: "Can I manage my parts and inventory?",
        a: "Parts are tracked as line items on quotes and job cards with cost and markup. Dedicated inventory management (stock levels, supplier ordering) is on the product roadmap.",
      },
      {
        q: "Does it work for panel beaters and tyre fitment centres, or only mechanical workshops?",
        a: "The system works for any vehicle service workshop — mechanical, panel and paint, tyre fitment, auto electrical, and roadworthy testing. The job card and DVI checklist are configurable. A few fields use automotive language but nothing is mechanics-specific.",
      },
    ],
  },
]

function FAQItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false)
  return (
    <div
      style={{
        border: '1px solid var(--border-subtle)', borderRadius: 10,
        background: '#fff', overflow: 'hidden',
      }}
      itemScope itemProp="mainEntity" itemType="https://schema.org/Question"
    >
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          width: '100%', textAlign: 'left', padding: '20px 24px',
          background: 'none', border: 'none', cursor: 'pointer',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16,
        }}
        aria-expanded={open}
      >
        <span
          itemProp="name"
          style={{ fontWeight: 600, fontSize: 15, color: 'var(--navy-700)', lineHeight: 1.4 }}
        >{q}</span>
        <svg
          width="18" height="18" viewBox="0 0 24 24" fill="none"
          stroke="var(--fg-3)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
          style={{
            flexShrink: 0,
            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 200ms ease-out',
          }}
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      {(
        <div hidden={!open}
          style={{ padding: '0 24px 20px' }}
          itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer"
        >
          <p itemProp="text" style={{ fontSize: 14, color: 'var(--fg-2)', lineHeight: 1.8, margin: 0 }}>
            {a}
          </p>
        </div>
      )}
    </div>
  )
}

export function FAQPage() {

  const { navigate } = useRouter()

  return (
    <MarketingLayout>
      {/* ── Hero ──────────────────────────────────────────────────────────── */}
      <section style={{
        background: 'var(--navy-900, #0d1b2e)',
        padding: '80px var(--space-6) 64px',
        textAlign: 'center',
      }}>
        <div style={{ maxWidth: 720, margin: '0 auto' }}>
          <h1 style={{
            fontFamily: 'var(--font-display)', fontSize: 'clamp(32px, 5vw, 52px)',
            fontWeight: 700, color: '#fff', letterSpacing: '-0.03em',
            lineHeight: 1.1, marginBottom: 20,
          }}>Frequently asked questions</h1>
          <p style={{ fontSize: 18, color: 'var(--neutral-400)', lineHeight: 1.6 }}>
            Real answers to questions from South African workshop owners.
          </p>
        </div>
      </section>

      {/* ── FAQ content ───────────────────────────────────────────────────── */}
      <section
        style={{ padding: '80px var(--space-6)', background: 'var(--bg-1)' }}
        itemScope itemType="https://schema.org/FAQPage"
      >
        <div style={{ maxWidth: 840, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 64 }}>
          {FAQ_SECTIONS.map(section => (
            <div key={section.section}>
              <h2 style={{
                fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 700,
                color: 'var(--navy-700)', marginBottom: 20,
                paddingBottom: 16, borderBottom: '2px solid var(--border-subtle)',
              }}>{section.section}</h2>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {section.items.map(item => (
                  <FAQItem key={item.q} q={item.q} a={item.a} />
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Still have questions ──────────────────────────────────────────── */}
      <section style={{ padding: '72px var(--space-6)', background: 'var(--navy-900, #0d1b2e)', textAlign: 'center' }}>
        <div style={{ maxWidth: 600, margin: '0 auto' }}>
          <h2 style={{
            fontFamily: 'var(--font-display)', fontSize: 'clamp(24px, 4vw, 36px)',
            fontWeight: 700, color: '#fff', letterSpacing: '-0.02em', marginBottom: 16,
          }}>Still have questions?</h2>
          <p style={{ fontSize: 16, color: 'var(--neutral-400)', marginBottom: 36 }}>
            Call or WhatsApp us during SA business hours. A human in Johannesburg will answer.
          </p>
          <button
            onClick={() => navigate('/contact')}
            style={{
              padding: '14px 32px', borderRadius: 8, fontSize: 16, fontWeight: 600,
              background: 'var(--orange-500, #D96E1A)', border: 'none',
              color: '#fff', cursor: 'pointer',
            }}
          >Get in touch</button>
        </div>
      </section>
    </MarketingLayout>
  )
}
